import airbrake from "utils/airbrake"
import "components/icon"
import { BaseElement } from "utils/base-element"
import ForecastTableData from "./data"
import ForecastTableScroll from "./scroll"
import ForecastTableContent from "./content"
import "./forecast-table.scss"
import "components/forecast-table/custom.scss"
import "./static/icon-chevron-circle-left.svg"
import "./static/icon-chevron-circle-right.svg"
import "./static/icon-snowflake.svg"

require.context("./static", false, /.{1,}/)

export type Column = { date: string; left: number; right: number }

const DAY_HEAD = '[data-row="days"] > td[data-date]'

// The FCGON fields the server always sets for the forecast page. Only snow declares window.FCGON
// (in its own components/incentive/incentive.ts), so the table reads it through this type instead
interface ForecastFCGON {
  dataUrl: string
  level: string
  levels?: string[]
  model: string
}

declare global {
  interface HTMLElementTagNameMap {
    "fc-forecast-table": ForecastTable
  }
  interface Window {
    // Other sites' code, and fc_core's own elevation-switcher/model-switcher components, call
    // these directly rather than through this element
    changeElevation?: (elev: string) => Promise<void>
    changeModel?: (model: string) => Promise<void>
  }
}

// fc_core's forecast table as a custom element, so other components can find and use it directly
// via document.querySelector("fc-forecast-table") instead of reaching into its DOM and classes.
// Other sites' code (and fc_core's own elevation-switcher/model-switcher) still call the
// window.changeElevation/changeModel globals this sets, for backwards compatibility
export class ForecastTable extends BaseElement {
  private content?: ForecastTableContent
  private tableScroll?: ForecastTableScroll

  // Reads the [data-content] tables and FCGON forecast metadata the server already rendered into
  // this element. Only correct for markup that's already present when this runs, i.e. the custom
  // element upgrading already-parsed DOM - not an element created detached and filled in after
  constructor() {
    super()

    const tables = Array.from(this.querySelectorAll("[data-content]"))
    if (!tables.length) return

    const { FCGON } = window as unknown as { FCGON: ForecastFCGON }
    const { dataUrl: url, level, levels, model } = FCGON
    const data = new ForecastTableData(url, levels || [level], level, model)
    this.content = new ForecastTableContent(
      tables,
      data,
      level,
      model,
      (detail: number, atLevel: string) =>
        this.emit("forecast-table:detail-increase", { detail, level: atLevel }),
    )
    this.tableScroll = new ForecastTableScroll(this, this.content, (atLevel: string) =>
      this.emit("forecast-table:hindcast-request", { level: atLevel }),
    )

    window.setTimeout(() => this.classList.add("is-current-hour-hidden"), 5 * 60 * 1000)

    window.changeElevation = (elev) => this.content!.changeLevel(elev)
    window.changeModel = (toModel) => this.content!.changeModel(toModel)

    new MutationObserver(() => this.emit("forecast-table:change")).observe(this.contentElement, {
      childList: true,
      subtree: true,
    })
    this.contentElement.addEventListener("scroll", () => this.emit("forecast-table:scroll"), {
      passive: true,
    })

    this.wireExpandAllButtons()
  }

  // The page's "expand all"/"collapse all" button, which isn't inside this element
  private wireExpandAllButtons() {
    document.querySelectorAll<HTMLButtonElement>("[data-table-expand-all]").forEach((button) => {
      let loading = false
      let expanded = false
      button.addEventListener("click", async (event) => {
        event.preventDefault()
        if (loading) return

        loading = true
        button.classList.add("is-loading")

        try {
          await (expanded ? this.content?.collapseAll() : this.content?.expandAll())
          expanded = !expanded
          // eslint-disable-next-line no-param-reassign
          button.textContent = button.dataset[expanded ? "collapse" : "expand"] || ""
        } catch (e) {
          airbrake.notify(e)
        }

        button.classList.remove("is-loading")
        loading = false
      })
    })
  }

  get level(): string | undefined {
    return this.content?.level
  }

  // The content div other code may still need for its own data attributes, e.g. the elevation
  // switcher/hindcast effect's quiet-past flag (utils/forecast-table-settling.ts)
  get contentElement(): HTMLElement {
    return this.element("content") as HTMLElement
  }

  get scrollLeft(): number {
    return this.contentElement.scrollLeft
  }

  set scrollLeft(value: number) {
    this.contentElement.scrollLeft = value
  }

  dayPositions(): Column[] {
    const heads = this.contentElement.querySelectorAll<HTMLElement>(DAY_HEAD)
    return Array.from(heads).map((el) => {
      const rect = el.getBoundingClientRect()
      return { date: el.dataset.date as string, left: rect.left, right: rect.right }
    })
  }

  // The row labels sit over the table's left edge
  visibleBounds(): { left: number; right: number } {
    const rect = this.contentElement.getBoundingClientRect()
    const labels = this.contentElement.querySelector<HTMLElement>(
      `.${this.elementClassName("header-container")}`,
    )
    const left = Math.max(rect.left, labels?.getBoundingClientRect().right ?? rect.left)
    return { left, right: rect.right }
  }

  todayElement(): HTMLElement | null {
    return this.contentElement.querySelector<HTMLElement>("[data-first-cell]")
  }

  get firstForecastDate(): string | undefined {
    return this.todayElement()?.dataset.date
  }

  get snowRow(): HTMLElement | null {
    return this.contentElement.querySelector<HTMLElement>('tr[data-row="snow"]')
  }

  scrollToDate(date: string, behavior: ScrollBehavior): boolean {
    const column = this.dayPositions().find((c) => c.date === date)
    if (!column) return false

    const left = Math.max(
      0,
      column.left - this.visibleBounds().left + this.contentElement.scrollLeft,
    )
    try {
      this.contentElement.scroll({ left, top: 0, behavior })
    } catch {
      this.contentElement.scrollLeft = left
    }
    return true
  }

  // Resolves once fc_core's own hindcast batch settles: `loaded` is whether this call actually
  // inserted past days (false when none are left, the user is paywalled, or a batch is already in
  // flight), `hasPast` is whether any remain for a further call, `locked` whether the paywall ended it
  async loadPastBatch(): Promise<{ loaded: boolean; hasPast: boolean; locked: boolean }> {
    if (!this.tableScroll) return { loaded: false, hasPast: false, locked: false }

    const loaded = await this.tableScroll.loadPast()
    return {
      loaded: Boolean(loaded),
      hasPast: Boolean(this.tableScroll.hasPast),
      locked: Boolean(this.tableScroll.locked),
    }
  }

  async changeElevation(elev: string): Promise<void> {
    await this.content?.changeLevel(elev)
  }
}

BaseElement.define("forecast-table", ForecastTable)
